// tiers.js — canonical tier registry + resolver.
// Session 46 Phase B1. The ONLY place tier logic should live.
//
// Every consumer (chat, image gen, settings, paywall, banner system)
// reads through effectiveTier(profile). Never branch on raw
// seat_type or seat_type_override columns directly in consumers.
//
// Precedence:
//   role=owner  >  banned override  >  BYOK (paid+key)  >  other override  >  seat_type  >  trial fallback
//
// Dependency note: the resolver is pure (no I/O). Callers must
// enrich the profile with `byok_key_present: boolean` before
// calling. Use `enrichProfileForTier()` from lib/tier-helpers.js
// (server-side only) or set it manually on the client.

import { PLANS } from "./ful-legend";

// Individual tier definitions — each tier carries enough to run the
// chat route without round-tripping for another lookup. Pricing and
// model fields reuse PLANS (ful-legend) as source of truth; `role`
// and overlay-only tiers (special, banned) are defined here.

export const TIERS = {
  trial: {
    key: "trial",
    label: PLANS.trial.label,
    role: "capped",
    messages: PLANS.trial.fulTotal,   // 150 (one-shot over 14 days)
    model: PLANS.trial.model,
    maxTokens: PLANS.trial.maxTokens,
    compressAt: PLANS.trial.compressAt,
    isByok: false,
    price: PLANS.trial.price,           // 0 (free trial; MRR calc reads this)
    priceLabel: PLANS.trial.priceLabel, // "Free"
  },
  standard: {
    key: "standard",
    label: PLANS.standard.label,
    role: "paid",
    messages: PLANS.standard.fulPerMonth, // 450/mo
    model: PLANS.standard.model,
    maxTokens: PLANS.standard.maxTokens,
    compressAt: PLANS.standard.compressAt,
    isByok: false,
    price: PLANS.standard.price,           // 9 (paid-tier consumer reads)
    priceLabel: PLANS.standard.priceLabel, // "$9/mo"
  },
  pro: {
    key: "pro",
    label: PLANS.pro.label,
    role: "paid",
    messages: PLANS.pro.fulPerMonth,      // 800/mo
    model: PLANS.pro.model,
    maxTokens: PLANS.pro.maxTokens,
    compressAt: PLANS.pro.compressAt,
    isByok: false,
    price: PLANS.pro.price,           // 15
    priceLabel: PLANS.pro.priceLabel, // "$15/mo"
  },
  special: {
    key: "special",
    label: "Special",
    role: "special",                      // no paywall, no counter
    messages: Infinity,
    model: PLANS.guest?.model || "claude-sonnet-4-6",
    maxTokens: PLANS.guest?.maxTokens || 2048,
    compressAt: PLANS.guest?.compressAt || 80000,
    isByok: false,
  },
  banned: {
    key: "banned",
    label: "Paused",
    role: "denied",
    messages: 0,
    model: null,
    maxTokens: 0,
    compressAt: 0,
    isByok: false,
  },
  owner: {
    key: "owner",
    label: PLANS.owner.label,
    role: "infinite",
    messages: Infinity,
    model: PLANS.owner.model,
    maxTokens: PLANS.owner.maxTokens,
    compressAt: PLANS.owner.compressAt,
    isByok: false,
  },
  byok: {
    key: "byok",
    label: PLANS.byok.label,
    role: "infinite",
    messages: Infinity,
    model: PLANS.byok.model,
    maxTokens: PLANS.byok.maxTokens,
    compressAt: PLANS.byok.compressAt,
    isByok: true,
  },
};

// Resolver — pure, synchronous. Caller must enrich profile with
// `byok_key_present: boolean` if BYOK resolution matters.
//
// Reads only the columns documented in the spec; never touches
// seat_type values outside {trial, standard, pro} (the narrowed
// CHECK constraint guarantees nothing else gets written).
export function effectiveTier(profile) {
  if (!profile) return TIERS.trial;

  // 1. Owner role trumps everything. profiles.role is source of truth.
  if (profile.role === "owner") return TIERS.owner;

  // 2. Ban overlay trumps every non-owner state.
  if (profile.seat_type_override === "banned") return TIERS.banned;

  // 3. BYOK overlay: paid subscribers with a live key get Opus.
  //    Trial / lapsed / banned / special users with a key get NOTHING.
  const isPaid = profile.seat_type === "standard" || profile.seat_type === "pro";
  if (profile.byok_key_present && isPaid) return TIERS.byok;

  // 4. Other owner grants (special).
  if (profile.seat_type_override) {
    return TIERS[profile.seat_type_override] || TIERS.trial;
  }

  // 5. Stripe truth.
  return TIERS[profile.seat_type] || TIERS.trial;
}

// ─────────────────────────────────────────────────────────────────────
//  routeModelForRequest — opus_on_simple pre-flight downgrade
// ─────────────────────────────────────────────────────────────────────
//
// Per-request model routing. Default: return tier.model (current
// behavior). Override: when tier is owner/byok (the only Opus-using
// tiers) AND the request looks "simple" → downgrade to Sonnet for
// THIS request.
//
// Conservative heuristic — err toward Opus on uncertainty. The owner
// is the heaviest Opus user and would feel quality regression first
// if this is too aggressive. Threshold tightened to 100 tokens
// (≈400 chars) per Collin's spec, smaller blast radius until
// telemetry confirms quality holds.
//
// Kill switch: process.env.SPEND_DOWNGRADE_DISABLED=1 disables the
// downgrade entirely. Set in Vercel if owner sees quality regress
// before per-conversation auto-recovery ships (parked).
//
// Inputs:
//   tier            — TIER object from effectiveTier()
//   userMessage     — string (the latest user message text)
//   hasAttachments  — bool (file dropped in this turn)
//   ecosystemKws    — { [name]: string[] } map (lib/ecosystem-keywords)
//
// Output:
//   { model: string, downgraded: bool, reason?: string }
//   - downgraded: false → return tier.model unchanged, no reason
//   - downgraded: true  → return Sonnet model, reason = why we
//     decided this request was simple (for telemetry)

const SONNET_MODEL = "claude-sonnet-4-6";
const SIMPLE_CHAR_THRESHOLD = 400; // ≈100 tokens

// Save-intent verbs — any of these in the user message blocks downgrade.
// Even a short "save it" or "drop it in HBBEVCO" needs Opus's
// categorization reasoning + tool routing accuracy. Tool-using turns
// stay on Opus regardless of length.
const SAVE_INTENT_RX = /\b(save|file|note|log|remember|track|drop|attach|upload|store|catalog|index)\b/i;

// Multi-step signals — sentences that imply a chain of operations.
// Sonnet can handle one-shots reliably but multi-step reasoning is
// where Opus pulls ahead.
const MULTI_STEP_RX = /\b(then|and also|after that|after which|followed by|step\s+\d|step by step|first[, ]+(?:then|next)|both .* and)\b/i;

export function routeModelForRequest(tier, userMessage, hasAttachments = false, ecosystemKws = null) {
  // Kill switch — env var disables downgrade entirely.
  if (process.env.SPEND_DOWNGRADE_DISABLED === "1") {
    return { model: tier.model, downgraded: false };
  }

  // Only owner + byok tiers use Opus — those are the only ones we can
  // downgrade. Sonnet-tier users (trial/standard/pro/founder/guest)
  // are already on Sonnet; routing is a no-op for them.
  const isOpusTier = tier?.model === "claude-opus-4-6";
  if (!isOpusTier) {
    return { model: tier.model, downgraded: false };
  }

  // Banned tier has no model — pass through unchanged.
  if (!tier.model) {
    return { model: tier.model, downgraded: false };
  }

  const text = (typeof userMessage === "string" ? userMessage : "").trim();

  // Empty message — keep Opus (probably a tool-only or special turn).
  if (!text) {
    return { model: tier.model, downgraded: false };
  }

  // File attached — keep Opus. Eager-save flow needs the model to
  // describe + categorize + tag in one turn; Sonnet's categorization
  // accuracy under that load isn't yet validated.
  if (hasAttachments) {
    return { model: tier.model, downgraded: false };
  }

  // Length gate — too long → keep Opus.
  if (text.length > SIMPLE_CHAR_THRESHOLD) {
    return { model: tier.model, downgraded: false, reason: "long" };
  }

  // Save-intent verbs → keep Opus.
  if (SAVE_INTENT_RX.test(text)) {
    return { model: tier.model, downgraded: false, reason: "save_intent" };
  }

  // Multi-step signals → keep Opus.
  if (MULTI_STEP_RX.test(text)) {
    return { model: tier.model, downgraded: false, reason: "multi_step" };
  }

  // Tool-trigger keywords — if any ECOSYSTEM_KEYWORDS match, an
  // integration tool will likely fire. Keep Opus for tool-using turns.
  if (ecosystemKws && typeof ecosystemKws === "object") {
    const lower = text.toLowerCase();
    for (const [, kws] of Object.entries(ecosystemKws)) {
      if (!Array.isArray(kws)) continue;
      for (const kw of kws) {
        if (typeof kw !== "string" || !kw) continue;
        if (lower.includes(kw.toLowerCase())) {
          return { model: tier.model, downgraded: false, reason: "tool_keyword" };
        }
      }
    }
  }

  // All gates passed — downgrade to Sonnet for this request.
  return {
    model: SONNET_MODEL,
    downgraded: true,
    reason: "simple_chat",
  };
}
